import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import colors from 'theme/colors';
import Button from 'components/_UI/Button';
import Fieldset from 'components/_UI/Fieldset';
import Switch from 'components/_UI/Switch';
import Text from 'components/_UI/Text';
import breakpoints from 'theme/breakpoints';
import { useEstablishment } from 'context/establishments/Establishment';
import masks from 'helpers/masks';
import { MinusSquare, PlusSquare } from 'lucide-react';
import { panelAccent } from 'theme/panelAccent';

export default function Hours({ isEdit }) {
    const { establishment, setEstablishment } = useEstablishment();

    const initialState = {
        sunday: [],
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
    };

    const [days, setDays] = useState(initialState);
    const [allDayFlags, setAllDayFlags] = useState({
        sunday: false,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
    });

    useEffect(() => {
        if (isEdit) {
            const editedDays = {};
            const editedFlags = {};
            for (const day in isEdit) {
                if (isEdit[day] === null) {
                    editedDays[day] = [];
                    editedFlags[day] = false;
                } else if (isEdit[day].some(item => item["24h"])) {
                    editedDays[day] = [{ start: '00:00', end: '00:00', allDay: true }];
                    editedFlags[day] = true;
                } else {
                    editedDays[day] = isEdit[day].map(item => ({
                        start: item.start || '',
                        end: item.end || '',
                        allDay: item.allDay || false,
                    }));
                    editedFlags[day] = false;
                }
            }
            setDays(editedDays);
            setAllDayFlags(editedFlags);
        }
    }, [isEdit]);

    useEffect(() => {
        setEstablishment({
            ...establishment,
            days
        });
    }, [days]);

    const handleSwitchChange = (day) => {
        setDays((prevDays) => ({
            ...prevDays,
            [day]: prevDays[day].length > 0 ? [] : [{ start: '', end: '', allDay: false }]
        }));
    };

    const handleAddField = (day) => {
        setDays((prevDays) => ({
            ...prevDays,
            [day]: [...prevDays[day], { start: '', end: '', allDay: false }]
        }));
    };

    const handleRemoveField = (day) => {
        setDays((prevDays) => ({
            ...prevDays,
            [day]: prevDays[day].slice(0, -1) // Remove the last item
        }));
    };

    const handleAllDayCheckboxChange = (day) => {
        const newValue = !allDayFlags[day];
        setAllDayFlags((prevFlags) => ({
            ...prevFlags,
            [day]: newValue
        }));

        setDays((prevDays) => ({
            ...prevDays,
            [day]: newValue
                ? [{ start: '00:00', end: '00:00', allDay: true }]
                : prevDays[day].map(item => ({ ...item, allDay: false, start: '', end: '' }))
        }));
    };

    const handleInputChange = (day, index, field, value) => {
        setDays((prevDays) => ({
            ...prevDays,
            [day]: prevDays[day].map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        }));
    };

    const renderTimeAllways= (day) => (
        <ContainerHeader>
            {days[day].length > 0 && (
                <CheckboxContainer>
                    
                    <CheckboxInput
                        type="checkbox"
                        checked={allDayFlags[day]}
                        onChange={() => handleAllDayCheckboxChange(day)}
                    />
                    
                    <CheckboxLabel>24 horas</CheckboxLabel>
                </CheckboxContainer>
            )}
            
        </ContainerHeader>
    );
    const renderTimeFields = (day) => (
        <>
            <ContentFieldsHours>
                {days[day].map((item, index) => (
                    <TimeFieldsContainer key={`${day}-${index}`}>
                        <StyledInput>
                            <Fieldset
                                label='Abertura'
                                layout='squired'
                                marginRight={'10px'}
                                disabled={item.allDay}
                                mask={masks.time}
                                value={item.start}
                                onChangeValue={(e) => handleInputChange(day, index, 'start', e)}
                            />
                        </StyledInput>
                        <StyledInput>
                            <Fieldset
                                label='Fechamento'
                                layout='squired'
                                disabled={item.allDay}
                                mask={masks.time}
                                value={item.end}
                                onChangeValue={(e) => handleInputChange(day, index, 'end', e)}
                            />
                        </StyledInput>
                    </TimeFieldsContainer>
                ))}
            </ContentFieldsHours>
            <StyledRow>
                <StyledButtonAdd>
                    <Button
                        onClick={() => handleAddField(day)}
                        style={{ marginTop: '10px' }}
                        variant={allDayFlags[day] ? 'disabled' : 'primary'}
                    >
                       <PlusSquare size={16} marginRight={'5px'} />  Adicionar campo
                    </Button>
                </StyledButtonAdd>
                {days[day].length > 1 && (
                    <ContentButtonRemove>
                        <Button
                            onClick={() => handleRemoveField(day)}
                            style={{ marginTop: '10px' }}
                            variant={allDayFlags[day] ? 'disabled' : 'danger'}
                        >
                         <MinusSquare size={16}/>   Remover último campo
                        </Button>
                    </ContentButtonRemove>
                )}
            </StyledRow>
        </>
    )
    return (
        <Column marginTop='40px' verticalCenter>
            <Text size='small' type='bold' color={panelAccent(colors.occasionalPurple)}>Horários de Funcionamento</Text>
            <Text size='small' color={colors.night} textAlign={'center'}>
                Selecione os dias de funcionamento do seu estabelecimento e preencha os horários.
            </Text>

            <SwitchsContainer>
                <Column marginTop={'20px'}>
                    <ContentDay checked={days.sunday.length > 0}>
                        <StyledRowDay>
                            <StyledContentDay>
                                <DaySwitch
                                    label='Domingo'
                                    checked={days.sunday.length > 0}
                                    onChange={() => handleSwitchChange('sunday')}
                                />
                                 <FlagDay checked={days.sunday.length > 0}>Dom</FlagDay>
                            </StyledContentDay>
                                {days.sunday.length > 0 && renderTimeAllways('sunday')}
                        </StyledRowDay>
                        {days.sunday.length > 0 && renderTimeFields('sunday')}
                    </ContentDay>
                </Column>

                <Column marginTop={'20px'}>
                    <ContentDay checked={days.monday.length > 0}>
                        <StyledRowDay>
                            <StyledContentDay>
                                <DaySwitch
                                    label='Segunda-feira'
                                    checked={days.monday.length > 0}
                                    onChange={() => handleSwitchChange('monday')}
                                />
                                 <FlagDay checked={days.monday.length > 0}>Seg</FlagDay>
                            </StyledContentDay>
                                {days.monday.length > 0 && renderTimeAllways('monday')}
                        </StyledRowDay>
                        {days.monday.length > 0 && renderTimeFields('monday')}
                    </ContentDay>
                </Column>

                <Column marginTop={'20px'}>
                    <ContentDay checked={days.tuesday.length > 0}>
                            <StyledRowDay>
                                <StyledContentDay>
                                    <DaySwitch
                                        label='Terça-feira'
                                        checked={days.tuesday.length > 0}
                                        onChange={() => handleSwitchChange('tuesday')}
                                    />
                                    <FlagDay checked={days.tuesday.length > 0}>Ter</FlagDay>
                                </StyledContentDay>
                                {days.tuesday.length > 0 && renderTimeAllways('tuesday')}
                            </StyledRowDay>
                        {days.tuesday.length > 0 && renderTimeFields('tuesday')}
                    </ContentDay>
                </Column>

                <Column marginTop={'20px'}>
                    <ContentDay checked={days.wednesday.length > 0}>
                        <StyledRowDay>
                            <StyledContentDay>
                                <DaySwitch
                                    label='Quarta-feira'
                                    checked={days.wednesday.length > 0}
                                    onChange={() => handleSwitchChange('wednesday')}
                                />
                                     <FlagDay checked={days.wednesday.length > 0}>Qua</FlagDay>
                                </StyledContentDay>
                                {days.wednesday.length > 0 && renderTimeAllways('wednesday')}
                            </StyledRowDay>
                        {days.wednesday.length > 0 && renderTimeFields('wednesday')}
                    </ContentDay>
                </Column>

                <Column marginTop={'20px'}>
                    <ContentDay checked={days.thursday.length > 0}>
                        <StyledRowDay>
                            <StyledContentDay>
                                <DaySwitch
                                    label='Quinta-feira'
                                    checked={days.thursday.length > 0}
                                    onChange={() => handleSwitchChange('thursday')}
                                />
                                <FlagDay checked={days.thursday.length > 0}>Qui</FlagDay>
                            </StyledContentDay>
                            
                                {days.thursday.length > 0 && renderTimeAllways('thursday')}
                            
                        </StyledRowDay>
                        
                            {days.thursday.length > 0 && renderTimeFields('thursday')}
                    </ContentDay>
                    
                </Column>

                <Column marginTop={'20px'}>
                <ContentDay checked={days.friday.length > 0}>
                        <StyledRowDay>
                            <StyledContentDay>
                                <DaySwitch
                                    label='Sexta-feira'
                                    checked={days.friday.length > 0}
                                    onChange={() => handleSwitchChange('friday')}
                                />
                                 <FlagDay checked={days.friday.length > 0}>Sex</FlagDay>
                            </StyledContentDay>
                                {days.friday.length > 0 && renderTimeAllways('friday')}
                        </StyledRowDay>
                        {days.friday.length > 0 && renderTimeFields('friday')}
                    </ContentDay>
                </Column>

                <Column marginTop={'20px'}>
                    <ContentDay checked={days.saturday.length > 0}>
                        <StyledRowDay>
                            <StyledContentDay>
                                <DaySwitch
                                    label='Sabado'
                                    checked={days.saturday.length > 0}
                                    onChange={() => handleSwitchChange('saturday')}
                                />
                                 <FlagDay checked={days.saturday.length > 0}>Sab</FlagDay>
                            </StyledContentDay>
                                {days.saturday.length > 0 && renderTimeAllways('saturday')}
                        </StyledRowDay>
                        {days.saturday.length > 0 && renderTimeFields('saturday')}
                    </ContentDay>
                </Column>
            </SwitchsContainer>
        </Column>
    );
}

// Componente para o switch de cada dia
function DaySwitch({ label, checked, onChange }) {
    return (
        <>
            <Switch label={label} checked={checked} onChange={onChange} />
        
        </>
    );
}

// Styles for the switches container
const SwitchsContainer = styled(Row)`
    flex-wrap: wrap;
    justify-content: space-around;
    width: 100%;
`;

const StyledRow = styled(Column)`
    @media screen and (min-width: ${breakpoints.lg}) {
        flex-direction: column;
        align-items: center;
        button:nth-child(2) {
            width: 65%;
        }
    }
`;


// Styles for the time fields container
const TimeFieldsContainer = styled(Row)`
    margin-top: 10px;
    width: 100%;
    justify-content: space-between;
    align-items: center;

   
`;

// Styles for the checkbox container
const CheckboxContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    width: 100px;
`;

const CheckboxInput = styled.input`
    margin-right: 8px;
    width: 15px;
    height: 15px;
`;

const CheckboxLabel = styled.label`
    cursor: pointer;
    color: ${colors.nightDark};
    font-size: 14px;
    
`;

const StyledInput = styled.div`
    width: 49%;

    & > div > label {
        font-size: 14px;
    }

   
`;

const StyledButtonAdd = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    flex-direction: row;
    align-items: center;
    & > button {
        width: 100%;
        & > svg {
            margin-right: 5px;
        }
    }
    @media screen and (min-width: ${breakpoints.sm}) {
        & > button {
            background: ${colors.neutralLight};
            border-radius: 4px;
            border: 1px dashed ${colors.specialPurple};
            color: rgba(147, 51, 234, 1);
            &:hover {
                background: rgb(250 245 255 / 0.5);
                border: 1px dashed ${colors.specialPurple};
            }
        }
    }
`

const ContentFieldsHours = styled(Column)`
    justify-content: center;
    width: 100%;
    align-items: center;
    background: ${colors.neutralLight};
    margin-top: 10px;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    padding: 10px;
    border: 1px solid ${colors.neutralDark};

`

const ContentDay = styled(Column)`
    width: 100%;
    box-shadow: 0px 0px 4px #ddd;
    min-height: 70px;
    height: auto;
    padding: 15px;
    border-radius: 8px;
   

    & > div > div >  label {
        display: flex;
        align-items: center;
        
    }

    
    ${({ checked }) => checked === true && `
        background: rgb(250 245 255 / 0.5);
        border: 1px solid ${colors.specialPurple};
    `};
`;


const ContainerHeader = styled.div`
    display: flex;
    width: auto;
    justify-content: space-between;
`;

const FlagDay = styled.div`
    display: flex;
    width: 45px;
    height: 20px;
    color: ${colors.nightDark};
    font-weight: 600;
    font-size: 12px;
    align-items: center;
    justify-content: center;
    background: #f8f9fa;
    border-radius: 8px;
    ${({checked}) => checked &&  `
        font-weight: 600;
        background: rgba(147, 51, 234, 1);
        color: ${colors.neutralBackground}
    `}
`;

const StyledContentDay = styled(Row)`
    display: flex;
    width: 50%;
    margin-right: 10px;
    justify-content: flex-start;
    align-items: center;
    & > :first-child {
        width: 130px;
        align-items: center;
        color: ${colors.nightDark};
        & > :nth-child(2) {
           color: ${colors.nightDark};
           font-weight: 600;
        }
        & > :first-child {
            display: flex;
            align-items: center;
          
        }
    }

   
`;

const StyledRowDay = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`

const ContentButtonRemove = styled.div`
    width: 100%;
    & > button {
        width: 100%;
        background: ${colors.neutralLight};
        border-radius: 4px;
        border: 1px dashed ${colors.specialPurple};
        color: red;
        & > svg {
            margin-right: 5px;
        }
        &:hover {
            background: rgb(250 245 255 / 0.5);
            border: 1px dashed red;
        }
    }
`